import { Box, Typography, Skeleton } from '@mui/material';
import { useRouteLoaderData } from 'react-router-dom';
import TimelineDot from '@mui/lab/TimelineDot';
import {
  TimelineConnector,
  TimelineContent,
  TimelineItem,
  TimelineOppositeContent,
  TimelineSeparator,
  Timeline,
} from '@mui/lab';
import { timelineOppositeContentClasses } from '@mui/lab/TimelineOppositeContent';
import useLiveData from '@/hooks/useLiveData';
import { useMemo, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import api from '@/services/ApiService';

const formatDate = (date) => {
  const d = new Date(date);
  return {
    display: `${d.getDate().toString().padStart(2, '0')}.${(d.getMonth() + 1)
      .toString()
      .padStart(2, '0')}.${d.getFullYear()} ${d
      .getHours()
      .toString()
      .padStart(2, '0')}:${d
      .getMinutes()
      .toString()
      .padStart(2, '0')}:${d.getSeconds().toString().padStart(2, '0')} Uhr`,
    timestamp: d.getTime(),
  };
};

const formatValue = (val) => {
  if (val === null || val === undefined) return 'leer';
  if (typeof val === 'boolean') return val ? 'Ja' : 'Nein';
  if (typeof val === 'object') {
    try {
      return (
        JSON.stringify(val).substring(0, 50) +
        (JSON.stringify(val).length > 50 ? '...' : '')
      );
    } catch (e) {
      return '[Komplexes Objekt]';
    }
  }
  return String(val);
};

const defaultFormatEntry = (entry) => {
  if (entry.operationType === 'insert') {
    return {
      title: 'Erstellt',
      description: ['Dokument initial angelegt'],
    };
  }

  if (entry.operationType === 'update' && entry.updateDescription) {
    const { updatedFields, removedFields } = entry.updateDescription;
    const changesList = [];

    const ignoredFields = [
      '_id',
      'uuid',
      'schema',
      'updatedAt',
      'createdAt',
      'user',
      'changedAt',
      '__v',
    ];

    const processField = (path, value, isRemoval = false) => {
      const parts = path.split('.');
      if (parts[0] === 'contractItems' && !isNaN(parseInt(parts[1]))) {
        const index = parseInt(parts[1]);
        if (isRemoval && parts.length === 2) {
          changesList.push({
            index: index,
            text: `Position ${index + 1} wurde entfernt`,
          });
          return;
        }

        const fieldKey = parts[2];

        if (!fieldKey || fieldKey === 'uuid') return;
        const displayKey = parts.slice(2).join('.');

        if (isRemoval) {
          changesList.push({
            index: index,
            text: `Pos. ${index + 1}: ${displayKey} gelöscht`,
          });
        } else {
          changesList.push({
            index: index,
            text: `Pos. ${index + 1}: ${displayKey} -> ${formatValue(value)}`,
          });
        }
      } else {
        const fieldKey = parts[0];
        if (!ignoredFields.includes(fieldKey)) {
          if (isRemoval) {
            changesList.push({
              index: -1,
              text: `Allgemein: ${parts.join('.')} gelöscht`,
            });
          } else {
            changesList.push({
              index: -1,
              text: `Allgemein: ${parts.join('.')} -> ${formatValue(value)}`,
            });
          }
        }
      }
    };

    if (updatedFields) {
      Object.entries(updatedFields).forEach(([path, value]) => {
        processField(path, value, false);
      });
    }

    if (removedFields && Array.isArray(removedFields)) {
      removedFields.forEach((path) => {
        processField(path, null, true);
      });
    }

    changesList.sort((a, b) => a.index - b.index);
    const descriptionLines = changesList.map((item) => item.text);
    if (descriptionLines.length === 0) {
      return {
        title: 'Aktualisiert',
        description: ['Interne System-Aktualisierung'],
      };
    }

    return {
      title: 'Änderung',
      description: descriptionLines,
    };
  }

  return {
    title: 'Änderung protokolliert',
    description: ['-'],
  };
};

const History = ({ documentId, formatEntry = defaultFormatEntry }) => {
  const { users } = useRouteLoaderData('auth');
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (!documentId) return;

    (async () => {
      setLoading(true);
      try {
        const {
          data: { result },
        } = await api.get(`/history?documentId=${documentId}`);
        setHistory(result || []);
      } catch (error) {
        toast.error(`Fehler beim Laden der Historie: ${error.message}`);
      } finally {
        setLoading(false);
      }
    })();
  }, [documentId]);

  useLiveData('history');

  const mergedData = useMemo(() => {
    if (!history) return [];

    return history
      .map((entry) => {
        const owner = users?.find((u) => u._id === entry.user);
        const formatted = formatEntry(entry);
        const dateInfo = formatDate(entry.changedAt);
        let descArray = [];
        if (Array.isArray(formatted.description)) {
          descArray = formatted.description;
        } else if (formatted.description) {
          descArray = [formatted.description];
        }

        return {
          id: entry._id,
          dateDisplay: dateInfo.display,
          timestamp: dateInfo.timestamp,
          user: owner
            ? `${owner.firstName} ${owner.lastName}`
            : 'Unbekannter Nutzer',
          title: formatted.title,
          description: descArray,
        };
      })
      .sort((a, b) => b.timestamp - a.timestamp);
  }, [history, users, formatEntry]);

  return (
    <Box sx={{ width: '100%', padding: 2 }}>
      <Box sx={{ overflowY: 'auto' }}>
        <Timeline
          position="right"
          sx={{
            [`& .${timelineOppositeContentClasses.root}`]: {
              flex: 0.25,
            },
            padding: 0,
          }}
        >
          {loading ? (
            Array.from(new Array(4)).map((_, index) => (
              <TimelineItem key={`skeleton-${index}`}>
                <TimelineOppositeContent>
                  <Skeleton
                    variant="text"
                    width={80}
                    height={15}
                    sx={{ ml: 'auto' }}
                  />
                </TimelineOppositeContent>
                <TimelineSeparator>
                  <TimelineDot sx={{ bgcolor: 'action.disabled' }} />
                  {index < 3 && <TimelineConnector />}
                </TimelineSeparator>
                <TimelineContent>
                  <Skeleton variant="text" width={120} height={20} />
                  <Skeleton variant="text" width="60%" height={20} />
                </TimelineContent>
              </TimelineItem>
            ))
          ) : (
            <>
              {mergedData.map((item) => (
                <TimelineItem key={item.id}>
                  <TimelineOppositeContent color="textSecondary">
                    <Typography
                      variant="caption"
                      display="block"
                      color="textSecondary"
                    >
                      {item.dateDisplay}
                    </Typography>
                  </TimelineOppositeContent>
                  <TimelineSeparator>
                    <TimelineDot color="primary" variant="outlined" />
                    <TimelineConnector />
                  </TimelineSeparator>
                  <TimelineContent sx={{ py: '12px', px: 2 }}>
                    <Typography
                      variant="caption"
                      color="primary"
                      sx={{ fontWeight: 'bold' }}
                    >
                      {item.user}
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 'bold', mb: 0.5 }}
                    >
                      {item.title}
                    </Typography>

                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                      }}
                    >
                      {item.description.map((line, idx) => (
                        <Typography
                          key={idx}
                          variant="body2"
                          color="textSecondary"
                          sx={{
                            wordBreak: 'break-word',
                            fontSize: '0.85rem',
                            lineHeight: 1.4,
                            fontFamily: 'monospace',
                          }}
                        >
                          {line}
                        </Typography>
                      ))}
                    </Box>
                  </TimelineContent>
                </TimelineItem>
              ))}

              {!loading && mergedData.length === 0 && (
                <Typography
                  variant="body2"
                  sx={{ pl: 4, pt: 2, color: 'text.secondary' }}
                >
                  Keine Historie vorhanden.
                </Typography>
              )}
            </>
          )}
        </Timeline>
      </Box>
    </Box>
  );
};

export default History;
